import { useCallback } from 'react'

import { SupportChat } from '@verbit-ai/frontend-common'

import { useFeatureFlag, useFeatureFlagsAvailable } from 'src/contexts/FeatureFlagContext'
import { useUserContext } from 'src/contexts/UserProvider'
import { rollbarService } from 'src/services/RollbarService'
import { getEnv, getSendbirdAiAgentId, getSendbirdAppId, isChromakeyModeActive } from 'src/utils/env'

type HelpChatProps = {
    isAIPanelOpened: boolean
}

export const HelpChat = (props: HelpChatProps) => {
    const { isAIPanelOpened } = props
    const { user, customer } = useUserContext()
    const areFlagsLoaded = useFeatureFlagsAvailable()
    const isSendbirdChatOn = useFeatureFlag('sendbird_support_chat')

    const reportError = useCallback((message: string, error: unknown) => rollbarService.error(message, { error: String(error) }), [])

    // Until the flags arrive the flag reads as off: choosing then would load Salesforce for a customer who gets
    // Sendbird, and Salesforce restores its last open conversation window on its own once its script is in the page.
    if (!areFlagsLoaded || !user || isChromakeyModeActive || isAIPanelOpened || customer?.customizations?.hide_chat) {
        return null
    }

    const env = getEnv() === 'production' ? 'prod' : 'staging'
    const appId = getSendbirdAppId()
    const aiAgentId = getSendbirdAiAgentId()

    if (!isSendbirdChatOn || !appId || !aiAgentId) {
        return <SupportChat kind="salesforce" env={env} onError={reportError} />
    }

    // Platform ids: Platform's Sendbird webhook checks email, user id and current customer against
    // the Platform user and freezes the conversation on a mismatch.
    const context = {
        email: user.email,
        verbit_platform_user_id: String(user.userId),
        verbit_current_customer_id: String(user.customerId ?? ''),
        verbit_customer_name: customer?.name ?? '',
    }

    return (
        <SupportChat
            kind="sendbird"
            mode="authenticated"
            env={env}
            appId={appId}
            aiAgentId={aiAgentId}
            language="en-US"
            context={context}
            onError={reportError}
        />
    )
}
